(() => {
  'use strict'

  const ajaxUrl = (url) => (url.endsWith('.js') ? url : `${url}.js`)

  const requestOptions = (body) => ({
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  })

  const cartUi = {
    parser: new DOMParser(),
    summarySelectors: [
      '.cart-summary__header',
      '#cart-total-price',
      '#checkout',
      '.cart-summary__free-shipping',
    ],

    parseSections(cart) {
      const mainCartHtml = cart.sections && cart.sections['main-cart']
      const navigationHtml = cart.sections && cart.sections['navigation-main']

      if (!mainCartHtml || !navigationHtml) return null

      return {
        mainCart: this.parser.parseFromString(mainCartHtml, 'text/html'),
        navigation: this.parser.parseFromString(navigationHtml, 'text/html'),
      }
    },

    replaceAll(selector, sourceDocument) {
      const source = sourceDocument.querySelector(selector)
      if (!source) return

      document.querySelectorAll(selector).forEach((target) => {
        target.replaceWith(source.cloneNode(true))
      })
    },

    updateCartCount(navigationDocument) {
      const sourceCount = navigationDocument.querySelector('#cart-open-block .cart-count')

      document.querySelectorAll('#cart-open-block .cart-count').forEach((count) => {
        if (!sourceCount || sourceCount.classList.contains('visually-hidden')) {
          count.classList.add('visually-hidden')
          count.textContent = '0'
          return
        }

        count.classList.remove('visually-hidden')
        count.textContent = sourceCount.textContent
      })
    },

    renderEmptyCart(mainCartDocument, navigationDocument) {
      const sourceMainCart = mainCartDocument.querySelector('.shopify-section.cart')
      const targetMainCart = document.querySelector('.shopify-section.cart')

      if (sourceMainCart && targetMainCart) {
        targetMainCart.innerHTML = sourceMainCart.innerHTML
      }

      ;['.mini-cart__content', '.mini-cart__footer'].forEach((selector) => {
        const source = navigationDocument.querySelector(`#mini-cart ${selector}`)
        const target = document.querySelector(`#mini-cart ${selector}`)
        if (source && target) target.replaceWith(source.cloneNode(true))
      })
    },

    render(cart) {
      const documents = this.parseSections(cart)

      if (!documents) {
        window.location.reload()
        return
      }

      const sourceItems = documents.mainCart.querySelector('.cart-items-container')

      if (sourceItems) {
        document.querySelectorAll('.cart-items-container, .mini-cart--empty').forEach((target) => {
          target.replaceWith(sourceItems.cloneNode(true))
        })

        this.summarySelectors.forEach((selector) => {
          this.replaceAll(selector, documents.mainCart)
        })
      } else {
        this.renderEmptyCart(documents.mainCart, documents.navigation)
      }

      this.updateCartCount(documents.navigation)
      document.querySelectorAll('.cart-item .icon').forEach((icon) => icon.classList.add('loaded'))
      this.bindCartItems()
    },

    async changeLine(line, quantity, item) {
      const controls = item.querySelectorAll('button, input[name="quantity"]')
      controls.forEach((control) => control.setAttribute('disabled', 'disabled'))

      try {
        const response = await fetch(
          ajaxUrl(window.routes.cartChange),
          requestOptions({
            line,
            quantity,
            sections: 'main-cart,navigation-main',
            sections_url: window.location.pathname,
          }),
        )
        const cart = await response.json()

        if (!response.ok || cart.status) throw new Error(cart.description || window.cartStrings.error)
        this.render(cart)
      } catch (error) {
        const errorElement = item.querySelector('.cart-item__error')
        if (errorElement) errorElement.textContent = error.message || window.cartStrings.error
        controls.forEach((control) => control.removeAttribute('disabled'))
      }
    },

    bindCartItems() {
      document.querySelectorAll('.cart-item').forEach((item) => {
        if (item.dataset.crossSellCartBound === 'true') return

        const line = Number(item.dataset.variantId)
        if (!line) return

        item.dataset.crossSellCartBound = 'true'
        const quantityInput = item.querySelector('input[name="quantity"]')
        const plus = item.querySelector('button[name="plus"]')
        const minus = item.querySelector('button[name="minus"]')
        const remove = item.querySelector('.cart-item-delete, .cart-item__remove')

        if (plus && quantityInput) {
          plus.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopImmediatePropagation()
            this.changeLine(line, Number(quantityInput.value) + 1, item)
          })
        }

        if (minus && quantityInput) {
          minus.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopImmediatePropagation()
            this.changeLine(line, Math.max(0, Number(quantityInput.value) - 1), item)
          })
        }

        if (remove) {
          remove.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopImmediatePropagation()
            this.changeLine(line, 0, item)
          })
        }
      })
    },
  }

  class CartCrossSell extends HTMLElement {
    constructor() {
      super()
      this.handleClick = this.handleClick.bind(this)
      this.isAdding = false
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick)
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick)
    }

    async handleClick(event) {
      const button = event.target.closest('[data-cart-cross-sell-add]')
      if (!button || !this.contains(button) || this.isAdding) return

      event.preventDefault()
      event.stopImmediatePropagation()

      const variantId = Number(button.dataset.variantId)
      if (!variantId) return

      const label = button.querySelector('[data-cart-cross-sell-button-text]')
      const errorElement = button.parentElement.querySelector('.cart-cross-sell__error')
      const originalLabel = label ? label.textContent : ''

      this.isAdding = true
      button.disabled = true
      button.setAttribute('aria-busy', 'true')
      if (label) label.textContent = window.cartStrings.addingToCart
      if (errorElement) errorElement.textContent = ''

      try {
        const response = await fetch(
          ajaxUrl(window.routes.cartAdd),
          requestOptions({
            items: [{ id: variantId, quantity: 1 }],
            sections: 'main-cart,navigation-main',
            sections_url: window.location.pathname,
          }),
        )
        const cart = await response.json()

        if (!response.ok || cart.status) throw new Error(cart.description || window.cartStrings.error)
        cartUi.render(cart)
      } catch (error) {
        if (errorElement) errorElement.textContent = error.message || window.cartStrings.error
        if (label) label.textContent = originalLabel
        button.disabled = false
        button.removeAttribute('aria-busy')
      } finally {
        this.isAdding = false
      }
    }
  }

  if (!customElements.get('cart-cross-sell')) {
    customElements.define('cart-cross-sell', CartCrossSell)
  }
})()
